import { CreateDialogFieldCompanion, CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { isValidUrl } from 'components/forms/inputs/rhf-validators/validators';
import InfoTooltip from 'components/InfoTooltip';
import ContactDynamicMultiSelectInput from 'components/party/ContactDynamicMultiSelectInput';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import PermissionGuard from 'components/security/PermissionGuard';
import upperFirst from 'lodash/upperFirst';
import { useLazyGetMinAssignmentsListWithFiltersQuery } from 'store/api/assignments';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { RelationshipTypeCode } from 'types/contacts';
import { FeatureFlag } from 'types/features.ts';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import CorrespondencePageFormSendersOrRecipients from '../CorrespondencePageFormSendersOrRecipients';
import { CorrespondenceFormState } from './useCorrespondenceForm';

type FieldsProps = { form: CorrespondenceFormState };

// The form lists the General section's fields around the others, so each run of fields is its own piece.

/** The number (on edit), name, type, direction (fixed on edit), category and dates. */
export const GeneralFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    const { classifiers, directionTypeCode, nonDraftRequired, type } = form;
    return (
        <>
            {type === 'edit' ? (
                <TextInput
                    control={control}
                    name="readOnlyNumber"
                    label="correspondence.correspondence-number"
                    disabled
                />
            ) : null}
            <TextInput
                control={control}
                name="name"
                label="correspondence.correspondence-name"
                placeholder="forms.enter-name"
                required
            />
            <SelectInput
                control={control}
                setValue={setValue}
                name="typeCode"
                label="correspondence.correspondence-type"
                placeholder="correspondence.select-correspondence-type"
                options={getValidOptionsFromClassifier<string, string>(classifiers['CORRESPONDENCE_TYPE']) ?? []}
                getOptionLabel="label"
                getOptionIdentifier="value"
                required={nonDraftRequired}
            />
            {/* A radio group always validates as required; the block holds it in the create dialog's
                Required section. */}
            <CreateDialogRequiredBlock when>
                <RadioInput
                    control={control}
                    setValue={setValue}
                    name="directionTypeCode"
                    label="correspondence.direction"
                    options={
                        getValidOptionsFromClassifier<string, string>(classifiers['CORRESPONDENCE_DIRECTION']) ?? []
                    }
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    required
                    row
                    disabled={type === 'edit'}
                />
            </CreateDialogRequiredBlock>
            <SelectInput
                control={control}
                setValue={setValue}
                name="categoryTypeCode"
                label="correspondence.correspondence-category"
                placeholder="forms.select-category"
                options={
                    getValidOptionsFromClassifier<string, string>(classifiers['CORRESPONDENCE_CATEGORY_TYPE']) ?? []
                }
                getOptionLabel="label"
                getOptionIdentifier="value"
            />
            <DateInput
                control={control}
                name="date"
                label={`correspondence.${(directionTypeCode ?? '').toLocaleLowerCase()}-date`}
                placeholder="forms.select-date"
                required={nonDraftRequired}
            />
            <DateInput
                control={control}
                name="documentDate"
                label="correspondence.document-date"
                placeholder="forms.select-date"
                required={nonDraftRequired}
            />
        </>
    );
};

/** Official, and its reference number once it is. */
export const OfficialFields = ({ form }: FieldsProps): JSX.Element => {
    const { control } = form.methods;
    const { isOfficial } = form;
    return (
        <>
            {/* Unticking it takes the required reference away, so it stays beside the reference. */}
            <CreateDialogFieldCompanion field="officialReference">
                <CheckboxInput
                    control={control}
                    name="isOfficial"
                    label="correspondence.official"
                    rightLabelComponent={<InfoTooltip text="correspondence.tooltips.official-heightened-attention" />}
                />
            </CreateDialogFieldCompanion>
            {isOfficial ? (
                <TextInput
                    control={control}
                    name="officialReference"
                    label="correspondence.official-reference-number"
                    placeholder="correspondence.enter-official-reference-number"
                    required={isOfficial}
                    rightLabelComponent={<InfoTooltip text="correspondence.tooltips.official-reference-number" />}
                />
            ) : null}
        </>
    );
};

/** Our employees on the receiving (incoming) or sending end, and the outside senders or recipients. */
export const SendersFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    const { nonDraftRequired, recipientOrSenderString } = form;
    return (
        <>
            <ContactDynamicMultiSelectInput
                control={control}
                setValue={setValue}
                name="employees"
                label={t('correspondence.sender-recipient-employees', {
                    direction: upperFirst(recipientOrSenderString),
                })}
                shouldTranslateLabel={false}
                placeholder="forms.select-employees"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
            />
            {/* Unless the status is Draft every entry needs a sender or a company, and an address by the
                type. The type is required on that same condition, so the entries follow it into the create
                dialog's Required section as one block and no address leaves its sender. A companion rather
                than a required block, which would count every field of an entry as one to fill, or with
                `isEmpty` all the entries as one: in a companion each field counts by its own `required`. */}
            <CreateDialogFieldCompanion field="typeCode">
                <CorrespondencePageFormSendersOrRecipients
                    control={control}
                    setValue={setValue}
                    validationRequired={nonDraftRequired}
                />
            </CreateDialogFieldCompanion>
        </>
    );
};

export const NoteFields = ({ form }: FieldsProps): JSX.Element => (
    <TextAreaInput
        control={form.methods.control}
        name="note"
        label="correspondence.correspondence-notes"
        placeholder="forms.enter-notes"
        minRows={4}
    />
);

/** Handled and the status, which decides what the rest of the record requires. */
export const HandlingFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    const { classifiers } = form;
    return (
        <>
            <CheckboxInput
                control={control}
                name="isHandled"
                label="correspondence.handled"
                rightLabelComponent={
                    <InfoTooltip text="correspondence.tooltips.handled-ready-for-further-activities" />
                }
            />
            <SelectInput
                control={control}
                setValue={setValue}
                name="statusTypeCode"
                label="correspondence.status"
                placeholder="forms.select-status"
                options={getValidOptionsFromClassifier<string, string>(classifiers['CORRESPONDENCE_STATUS_TYPE']) ?? []}
                getOptionLabel="label"
                getOptionIdentifier="value"
            />
        </>
    );
};

/** The clients, the cases (a case adds its client) and, with assignments on, the cases' assignments. */
export const ConnectedClientCaseFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    const { matters, selectedClientIds, selectedMatterIds, handleOnMattersChange } = form;
    return (
        <>
            <ContactDynamicMultiSelectPicker
                control={control}
                name="clients"
                label="correspondence.client-name"
                placeholder="correspondence.select-client-name"
                setValue={setValue}
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: `relationshipTypeCode==${RelationshipTypeCode.CLIENT}`,
                }}
                isPickerModalOptionEnabled={(option) => option.relationshipTypeCode === RelationshipTypeCode.CLIENT}
                PickerModalComponent={ContactPickerModal}
            />
            <DynamicMultiSelectInput
                control={control}
                name="matters"
                label="correspondence.case"
                placeholder="correspondence.select-case"
                setValue={setValue}
                paginated={{
                    fetch: useLazyGetMattersListQuery,
                    searchField: ['fullName', 'clientParty.name'],
                    filters: selectedClientIds ? `clientParty.id=in=(${selectedClientIds})` : '',
                }}
                groupByField="clientParty.name"
                getOptionLabel="fullName"
                getOptionIdentifier="id"
                getSelectedValueStartAdornment={renderMatterIndicators}
                customOptionRenderer={renderMatterOptionWithRestrictedSelection}
                onChange={handleOnMattersChange}
            />
            {matters && matters.length > 0 && (
                <PermissionGuard
                    permissions={['READ.MATTER_ASSIGNMENTS']}
                    requiredFeature={FeatureFlag.CASE_ASSIGNMENT_ENABLED}
                >
                    <DynamicMultiSelectInput
                        setValue={setValue}
                        control={control}
                        name="assignments"
                        label="correspondence.form.assignments"
                        placeholder="correspondence.form.select-assignments"
                        paginated={{
                            fetch: useLazyGetMinAssignmentsListWithFiltersQuery,
                            searchField: ['name'],
                            filters: selectedMatterIds ? `matter.id=in=(${selectedMatterIds})` : '',
                            sort: ['matter.fullName,asc', 'name,asc'],
                            pageSize: 500,
                            isEnabled: !!selectedMatterIds,
                        }}
                        groupByField="matter.fullName"
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                    />
                </PermissionGuard>
            )}
        </>
    );
};

export const DmsFields = ({ form }: FieldsProps): JSX.Element => (
    <TextInput
        name="dmsLink"
        control={form.methods.control}
        label="correspondence.dms-link"
        validate={(input) => isValidUrl(input, t('validation.dms-link-must-be-valid'))}
    />
);

/** The General section: the record's own facts and its status. */
export const GeneralSectionFields = ({ form }: FieldsProps): JSX.Element => (
    <>
        <GeneralFields form={form} />
        <HandlingFields form={form} />
    </>
);
